import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import { ErrorMessage, Input, InputLabel } from "@shared/ui/Inputs";
import { MagnifyingGlass } from "phosphor-react";
import React, { useRef } from "react";
import RDSelect from "react-dropdown-select";
import { useIntl } from "react-intl";

import SingleSelectAsyncItem, {
  WaypointEventType,
} from "./SingleSelectAsyncItem";
import SingleSelectItem from "./SingleSelectItem";
import DropdownStyles from "../../../css/dropdowns.module.css";

const Select: any = (RDSelect as any).default ?? RDSelect;

export const DROPDOWN_POSITIONS = {
  BOTTOM: "bottom",
  TOP: "top",
  AUTO: "auto",
} as const;

export type DropdownPositionType =
  (typeof DROPDOWN_POSITIONS)[keyof typeof DROPDOWN_POSITIONS];

type SingleSelectDropdownPropsType = {
  keyId: string;
  label?: string;
  uppercaseLabel?: boolean;
  errorMessage?: string;
  placeholder?: string;
  options?: any[];
  values?: any[];
  width?: string;
  height?: string;
  disabled?: boolean;
  dropdownPosition?: DropdownPositionType;
  closeOnSelect?: boolean;
  searchable?: boolean;
  /** Overrides the in-panel search input placeholder when `searchable` is on. */
  searchPlaceholder?: string;
  dropdownHeight?: string;
  onChange?: (value: any[]) => void;
  onDropdownOpen?: () => void;
  customInputClasses?: string;
  className?: string;
  isAsync?: boolean;
  multiSelect?: boolean;
  asyncProps?: {
    isLoading?: boolean;
    onScrollToBottom?: (event: WaypointEventType) => void;
    onSearch?: (term: string) => void;
  };
  showAvatar?: boolean;
  /** Max-width class for option labels. Defaults to the DropdownItem cap. */
  maxOptionLabelWidth?: string;
  customOptionItem?: (option: any) => React.ReactNode;
  /** Show radio indicators on options instead of a checkmark (single-select). */
  showRadio?: boolean;
  renderRemoveButton?: boolean;
  onRemove?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  contentRenderer?: (args: { state: any }) => React.ReactNode;
};

const SingleSelectDropdown = ({
  keyId,
  label = "",
  uppercaseLabel = false,
  errorMessage = "",
  placeholder = "",
  options = [],
  values = [],
  disabled = false,
  dropdownPosition = DROPDOWN_POSITIONS.BOTTOM,
  closeOnSelect = true,
  searchable = false,
  searchPlaceholder = "",
  dropdownHeight = "200px",
  onChange = () => {},
  onDropdownOpen = () => {},
  width = "!w-full",
  height = "h-4xl",
  customInputClasses = "",
  className = "",
  isAsync = false,
  asyncProps = null,
  showAvatar = false,
  maxOptionLabelWidth = "",
  customOptionItem = null,
  showRadio = false,
  renderRemoveButton = false,
  onRemove = () => {},
  header = null,
  footer = null,
  multiSelect = false,
  contentRenderer: contentRendererProp,
  ...props
}: SingleSelectDropdownPropsType) => {
  const { messages } = useIntl();
  const searchInDropdownPanel = searchable && !multiSelect;
  const selectMethodsRef = useRef<{
    setSearch?: (event: { target: { value: string } }) => void;
  }>(null);

  const toSearchEvent = (value: string) => ({ target: { value } });

  const classes: string = `${width} ${height} !flex !items-center !px-md !py-0 font-manrope text-xs font-medium !border !border-solid !rounded-lg outline-0 transition-all duration-200 ease-in-out focus-within:!outline-0 focus-within:!border focus-within:!border-solid focus-within:!shadow-none hover:!outline-0 hover:!shadow-none aria-expanded:z-[99999] ${
    errorMessage
      ? "bg-primary !border-danger focus-within:!border-danger aria-expanded:!border-danger"
      : disabled
      ? "text-tertiary bg-accent !border-transparent !opacity-100"
      : (values.length
          ? "text-primary bg-primary !border-primary"
          : "text-secondary bg-accent !border-transparent") +
        " focus-within:!bg-primary focus-within:!border-brand aria-expanded:!bg-primary aria-expanded:!border-brand"
  }`;

  const renderDropdownSearch = (state: any, methods: any) => {
    if (!searchInDropdownPanel) return null;

    return (
      <div className="border-0 border-b border-solid border-primary px-md pb-sm pt-md">
        <Input
          value={state.search ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            methods.setSearch(toSearchEvent(e.target.value));
          }}
          placeholder={
            searchPlaceholder ||
            (messages as any)?.common?.search ||
            (messages as any)?.scheduling?.searchPlaceholder ||
            "Search"
          }
          leadingIconConfig={{
            icon: <MagnifyingGlass size={14} className="text-secondary" />,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    );
  };

  const renderCustomDropdown = ({ props: selectProps, state, methods }) => {
    selectMethodsRef.current = methods;
    const itemProps = {
      ...selectProps,
      searchable: searchInDropdownPanel || selectProps.searchable,
    };

    if (isAsync) {
      return (
        <>
          {header}
          {renderDropdownSearch(state, methods)}
          <SingleSelectAsyncItem
            props={itemProps}
            state={state}
            methods={methods}
            isLoading={asyncProps?.isLoading}
            onScrollToBottom={asyncProps?.onScrollToBottom}
            onSearch={asyncProps?.onSearch}
            showAvatar={showAvatar}
            maxOptionLabelWidth={maxOptionLabelWidth}
            customOptionItem={customOptionItem}
          />
          {footer}
        </>
      );
    }
    return (
      <>
        {header}
        {renderDropdownSearch(state, methods)}
        <SingleSelectItem
          props={itemProps}
          state={state}
          methods={methods}
          showAvatar={showAvatar}
          maxOptionLabelWidth={maxOptionLabelWidth}
          customOptionItem={customOptionItem}
          showRadio={showRadio}
          multiSelect={multiSelect}
        />
        {footer}
      </>
    );
  };

  const renderContentRenderer = ({ state }: { state: any }) => {
    if (!state.values.length) {
      return (
        <span className="text-xs font-medium text-secondary">
          {placeholder}
        </span>
      );
    }

    const selectedLabels = state.values.map((value) => value.label).join(", ");
    return (
      <span
        className="text-xs font-medium text-primary"
        // The label is truncated when it doesn't fit — keep the full text
        // reachable on hover.
        title={selectedLabels}
      >
        {selectedLabels}
      </span>
    );
  };

  const selectSearchable = searchInDropdownPanel ? false : searchable;

  const resolvedContentRenderer = multiSelect
    ? contentRendererProp ?? renderContentRenderer
    : selectSearchable
      ? contentRendererProp
      : contentRendererProp ?? renderContentRenderer;

  return (
    <div
      className={`relative flex flex-col gap-sm min-w-0${
        multiSelect
          ? " [&_.react-dropdown-select-content]:!min-w-0 [&_.react-dropdown-select-content]:!overflow-hidden [&_.react-dropdown-select-content]:!flex-1"
          : ""
      }${
        searchInDropdownPanel
          ? " [&_.react-dropdown-select-input]:!hidden [&_.react-dropdown-select-content]:!w-full"
          : ""
      }`}
    >
      {label && (
        <div className="w-fit">
          <InputLabel
            label={label}
            keyId={keyId}
            uppercaseLabel={uppercaseLabel}
          />
        </div>
      )}

      <Select
        multi={multiSelect}
        dropdownHandleRenderer={() => (
          <span className="flex items-center justify-center">
            <ChevronDownIcon
              width={16}
              height={16}
              className={disabled ? "text-disabled" : "text-secondary"}
            />
          </span>
        )}
        searchable={selectSearchable}
        className={`${customInputClasses || classes} ${
          DropdownStyles.dropdowns
        } ${className} ${values.length ? "has-value" : ""}`}
        contentRenderer={resolvedContentRenderer}
        placeholder={placeholder}
        options={options}
        dropdownHeight={dropdownHeight}
        closeOnSelect={closeOnSelect}
        values={values}
        onChange={onChange}
        dropdownRenderer={renderCustomDropdown}
        disabled={disabled}
        onDropdownOpen={() => {
          onDropdownOpen();
          if (searchInDropdownPanel) {
            requestAnimationFrame(() => {
              selectMethodsRef.current?.setSearch?.(toSearchEvent(""));
            });
          }
        }}
        dropdownPosition={dropdownPosition}
        {...props}
      />

      {renderRemoveButton && !!values.length && (
        <Button
          className="w-fit"
          onClick={() => onRemove()}
          text={messages["common"]["remove"]}
          variant={BUTTON_VARIANTS.LINK}
        />
      )}

      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};

export default SingleSelectDropdown;
